import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { QltUploadFile } from '../../api/repository/upload';
import { formatBytes } from '../../config/file-size';
import { ChevronLeftIcon, ChevronRightIcon, FileTextIcon, MicIcon } from '../../layout/icons';
import { ImageOverlay } from '../link/ImageOverlay';
import { MediaOverlay } from '../link/MediaOverlay';
import { fitScale } from '../link/mediaFit';
import { usePdfDocument } from '../pdf/usePdfDocument';
import { fileExtension, uploadViewer, type UploadViewer } from './validation';
import { useMsg } from '../../i18n/msg';

const MAX_RENDER_DPR = 1.5;

export interface UploadOverlayProps {
  file: QltUploadFile | null;
  /** Set when the caller already classified the file, e.g. a student's extensionless S3 key. */
  viewer?: UploadViewer;
  onClose: () => void;
}

// one entry point per uploaded file; keyed so a new file never inherits the last one's player state
export function UploadOverlay({ file, viewer: classified, onClose }: UploadOverlayProps) {
  if (!file) return null;
  const viewer = classified || uploadViewer(file);
  if (viewer === 'image') return <ImageOverlay image={file} onClose={onClose} />;
  if (viewer === 'video') return <VideoFileOverlay key={file.id} file={file} onClose={onClose} />;
  if (viewer === 'audio') return <AudioFileOverlay key={file.id} file={file} onClose={onClose} />;
  if (viewer === 'pdf') return <PdfFileOverlay key={file.id} file={file} onClose={onClose} />;
  return <FileCardOverlay file={file} onClose={onClose} />;
}

function VideoFileOverlay({ file, onClose }: { file: QltUploadFile; onClose: () => void }) {
  const { t } = useTranslation('board');
  const [failed, setFailed] = useState(false);
  return (
    <MediaOverlay title={file.name} bare={true} onClose={onClose}>
      <div className="hls-video">
        {failed ? <div className="hls-video__error"><strong>{t('upload.videoFailed.title')}</strong><p>{t('upload.videoFailed.body')}</p></div> : null}
        <video src={file.url} controls={true} playsInline={true} preload="metadata" onError={() => setFailed(true)} />
      </div>
    </MediaOverlay>
  );
}

function AudioFileOverlay({ file, onClose }: { file: QltUploadFile; onClose: () => void }) {
  const { t } = useTranslation('board');
  const [failed, setFailed] = useState(false);
  return (
    <MediaOverlay title={t('upload.audio')} fit="card" onClose={onClose}>
      <div className="upload-card">
        <div className="upload-card__head">
          <span className="upload-card__mark" aria-hidden={true}><MicIcon size={26} /></span>
          <span className="upload-card__meta"><strong title={file.name}>{file.name}</strong><span>{formatBytes(file.size)}</span></span>
        </div>
        <audio className="upload-card__audio" src={file.url} controls={true} preload="metadata" onError={() => setFailed(true)} />
        {failed ? <p className="upload-card__note upload-card__note--error" role="alert">{t('upload.audioFailed')}</p> : null}
      </div>
    </MediaOverlay>
  );
}

function FileCardOverlay({ file, onClose }: { file: QltUploadFile; onClose: () => void }) {
  const { t } = useTranslation('board');
  const extension = fileExtension(file.name).toUpperCase();
  return (
    <MediaOverlay title={t('upload.document')} fit="card" onClose={onClose}>
      <div className="upload-card">
        <div className="upload-card__head">
          <span className="upload-card__mark" aria-hidden={true}><FileTextIcon size={26} /></span>
          <span className="upload-card__meta"><strong title={file.name}>{file.name}</strong><span>{[extension, formatBytes(file.size)].filter(Boolean).join(' • ')}</span></span>
        </div>
        <p className="upload-card__note">{extension ? t('upload.fileNote', { type: extension }) : t('upload.fileNoteGeneric')}</p>
      </div>
    </MediaOverlay>
  );
}

function PdfFileOverlay({ file, onClose }: { file: QltUploadFile; onClose: () => void }) {
  const { t } = useTranslation('board');
  const tr = useMsg();
  const pdf = usePdfDocument(file.url);
  const [page, setPage] = useState(1);
  const [renderError, setRenderError] = useState(false);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const pageCount = pdf.document ? pdf.document.numPages : 0;
  // a window resize re-fits the page; panel toggles leave the height, the side that binds, alone
  const [resizeTick, setResizeTick] = useState(0);

  useEffect(() => {
    const onResize = () => setResizeTick((tick) => tick + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const document = pdf.document;
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    if (!document || !stage || !canvas) return undefined;
    let cancelled = false;
    let task: { cancel: () => void } | null = null;
    void document.getPage(page).then((proxy) => {
      if (cancelled) return;
      const natural = proxy.getViewport({ scale: 1 });
      const scale = fitScale({ width: stage.clientWidth, height: stage.clientHeight }, natural);
      const ratio = Math.min(window.devicePixelRatio || 1, MAX_RENDER_DPR);
      const viewport = proxy.getViewport({ scale: scale * ratio });
      canvas.width = Math.max(1, Math.round(viewport.width));
      canvas.height = Math.max(1, Math.round(viewport.height));
      canvas.style.width = `${Math.round(natural.width * scale)}px`;
      canvas.style.height = `${Math.round(natural.height * scale)}px`;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('no 2d context');
      const render = proxy.render({ canvasContext: context, viewport });
      task = render;
      return render.promise;
    }).then(() => { if (!cancelled) setRenderError(false); }).catch(() => {
      if (!cancelled) setRenderError(true);
    });
    return () => {
      cancelled = true;
      if (task) task.cancel();
    };
  }, [page, pdf.document, resizeTick]);

  const error = pdf.error || (renderError ? t('pdf.renderFailed') : '');
  return (
    <MediaOverlay title={file.name} onClose={onClose}>
      <div className="upload-pdf">
        <div ref={stageRef} className="upload-pdf__stage">
          <canvas ref={canvasRef} aria-label={t('pdf.pageOf', { page, total: pageCount || 1 })} />
          {pdf.loading ? <div className="upload-pdf__state">{pdf.progress === null ? t('pdf.loading') : t('pdf.loadingPercent', { percent: pdf.progress })}</div> : null}
          {error ? <div className="upload-pdf__state upload-pdf__state--error">{tr(error)}</div> : null}
        </div>
        {pageCount > 1 ? (
          <div className="upload-pdf__pager">
            <button type="button" aria-label={t('pdf.previous')} disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeftIcon size={20} /></button>
            <span aria-live="polite">{t('pdf.pageOf', { page, total: pageCount })}</span>
            <button type="button" aria-label={t('pdf.next')} disabled={page >= pageCount} onClick={() => setPage(page + 1)}><ChevronRightIcon size={20} /></button>
          </div>
        ) : null}
      </div>
    </MediaOverlay>
  );
}
